import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { companiesApi, type CreateCompanyInput } from '@/api/companies';
import type { CompanyMember, CompanyOperatorMode, KycDocKind } from '@/types/company';
import type { CreatePricingPresetInput } from '@/types/companyExtras';

export function useMyCompany() {
  return useQuery({
    queryKey: ['company', 'mine'],
    queryFn: () => companiesApi.getMyCompany(),
  });
}

export function useCreateCompany() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateCompanyInput) => companiesApi.create(input),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['company'] }); },
  });
}

export function useUpdateCompany(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Partial<CreateCompanyInput>) => companiesApi.update(id, input),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['company'] }); },
  });
}

export function useSubmitCompany() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => companiesApi.submitForReview(id),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['company'] }); },
  });
}

export function useUploadKycDoc(companyId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { kind: KycDocKind; fileUrl: string; notes?: string }) =>
      companiesApi.uploadKycDoc(companyId, payload),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['company'] }); },
  });
}

export function useCompanyMembers() {
  return useQuery({
    queryKey: ['company', 'members'],
    queryFn: () => companiesApi.listMembers(),
  });
}

export function useInviteMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { phone: string; role: CompanyMember['role'] }) =>
      companiesApi.inviteMember(payload),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['company', 'members'] }); },
  });
}

export function useUpdateOperatorMode(companyId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (operatorMode: CompanyOperatorMode) =>
      companiesApi.updateOperatorMode(companyId, { operatorMode }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['company'] }); },
  });
}

export function usePricingPresets() {
  return useQuery({
    queryKey: ['company', 'pricing-presets'],
    queryFn: () => companiesApi.listPricingPresets(),
  });
}

export function useCreatePricingPreset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreatePricingPresetInput) => companiesApi.createPricingPreset(input),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['company', 'pricing-presets'] }); },
  });
}

export function useArchivePricingPreset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (presetId: number) => companiesApi.archivePricingPreset(presetId),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['company', 'pricing-presets'] }); },
  });
}

export function useCompanyReviews() {
  return useQuery({
    queryKey: ['company', 'reviews'],
    queryFn: () => companiesApi.listReviews(),
  });
}

export function useCompanyCustomers() {
  return useQuery({
    queryKey: ['company', 'customers'],
    queryFn: () => companiesApi.listCustomers(),
  });
}

export function useCompanyCustomerDetail(customerUserId: string | null) {
  return useQuery({
    queryKey: ['company', 'customer', customerUserId],
    queryFn: () => companiesApi.getCustomerDetail(customerUserId!),
    enabled: !!customerUserId,
  });
}

export function useCompanyMoneySummary(params?: { from?: string; to?: string; currency?: string }) {
  return useQuery({
    queryKey: ['company', 'money-summary', params?.from, params?.to, params?.currency],
    queryFn: () => companiesApi.getMoneySummary(params),
  });
}
